/**
 * PII Monitoring Widget Component
 *
 * Displays Personal Identifiable Information monitoring categories:
 * - Aliases
 * - Email Addresses
 * - Phone Numbers
 * - Addresses
 *
 * Each category shows count, empty state, and "+ New" button
 */

import React, { useState } from 'react';
import { Plus, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';

interface PIIMonitoringWidgetProps {
  /** Optional record ID for data fetching */
  recordId?: string;
  /** Show active items count */
  showActiveItems?: boolean;
  /** Show recent alerts */
  showRecentAlerts?: boolean;
  /** Enable quick add functionality */
  enableQuickAdd?: boolean;
}

interface PIICategory {
  id: string;
  label: string;
  count: number;
  description: string;
}

/**
 * PII Monitoring Widget
 */
const PIIMonitoringWidget: React.FC<PIIMonitoringWidgetProps> = ({
  recordId,
  showActiveItems = true,
  showRecentAlerts = true,
  enableQuickAdd = true
}) => {
  // State for collapsed sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Mock PII categories with counts (will be replaced with real data from API)
  const categories: PIICategory[] = [
    {
      id: 'aliases',
      label: 'Aliases',
      count: 0,
      description: 'Alternative names used by the client'
    },
    {
      id: 'email-addresses',
      label: 'Email Addresses',
      count: 0,
      description: 'Email addresses to monitor across data broker sites'
    },
    {
      id: 'phone-numbers',
      label: 'Phone Numbers',
      count: 0,
      description: 'Phone numbers to track in monitoring scans'
    },
    {
      id: 'addresses',
      label: 'Addresses',
      count: 0,
      description: 'Physical addresses to monitor for exposure'
    }
  ];

  /**
   * Toggle section collapse state
   */
  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  /**
   * Handle "+ New" button click (placeholder for future implementation)
   */
  const handleAddNew = (categoryId: string) => {
    console.log(`Add new item to category: ${categoryId}`);
    // TODO: Open modal to add new PII item
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      {/* Widget Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          PII Monitoring
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Configure personal information to monitor during automated scans
        </p>
      </div>

      {/* Widget Content */}
      <div className="p-6">
        {/* Info Banner */}
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-start">
            <Lightbulb className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
                PII Monitoring Configuration
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Configure personal information to monitor during automated scans (Phase 2). Add items to each category below to enable comprehensive monitoring across data broker sites.
              </p>
            </div>
          </div>
        </div>

        {/* PII Categories */}
        <div className="space-y-4">
          {categories.map((category) => {
            const isCollapsed = collapsedSections.has(category.id);

            return (
              <div
                key={category.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Category Header */}
                <div
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => toggleSection(category.id)}
                >
                  <div className="flex items-center space-x-3">
                    <button
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(category.id);
                      }}
                    >
                      {isCollapsed ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronUp className="h-5 w-5" />
                      )}
                    </button>
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {category.label} ({category.count})
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {category.description}
                      </p>
                    </div>
                  </div>

                  {enableQuickAdd && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddNew(category.id);
                      }}
                      className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 rounded-md transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      New
                    </button>
                  )}
                </div>

                {/* Category Content */}
                {!isCollapsed && (
                  <div className="p-4 bg-white dark:bg-gray-800">
                    {category.count === 0 ? (
                      // Empty State
                      <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No {category.label.toLowerCase()} configured
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Click "New" to add {category.label.toLowerCase()} for monitoring
                        </p>
                      </div>
                    ) : (
                      // Items List (placeholder for future implementation)
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Items will be displayed here
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        {showRecentAlerts && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Recent Alerts
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No recent alerts. PII monitoring alerts will appear here once configured.
            </p>
          </div>
        )}

        {recordId && (
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-500">
            Record ID: {recordId}
          </div>
        )}
      </div>
    </div>
  );
};

export default PIIMonitoringWidget;
